/**
 * Deployment amount input for wizard Step 2.
 *
 * Shows the amount input with helpful context about the selected thesis.
 */
import type { ThesisAllocation } from '../../types/thesis'

interface AmountInputProps {
  amount: number
  onChange: (amount: number) => void
  thesis: ThesisAllocation | null
  portfolioValue: number
}

export function AmountInput({
  amount,
  onChange,
  thesis,
  portfolioValue,
}: AmountInputProps) {
  // Calculate suggested amount to reach target
  const suggestedAmount = thesis && thesis.target_allocation_pct !== null
    ? Math.max(
        0,
        (thesis.target_allocation_pct / 100) * portfolioValue - thesis.total_market_value
      )
    : 0

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {thesis && (
          <div className="mb-6 pb-4 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-500">Selected Thesis</h3>
            <p className="text-lg font-semibold text-gray-900">{thesis.thesis_name}</p>
            <div className="mt-2 flex items-center gap-4 text-sm">
              <span className="text-gray-500">
                Current: {thesis.current_allocation_pct.toFixed(1)}%
              </span>
              <span className="text-gray-500">
                Target: {thesis.target_allocation_pct?.toFixed(1) ?? '-'}%
              </span>
            </div>
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
            Amount to Deploy
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              id="amount"
              type="number"
              min={0}
              step={100}
              value={amount || ''}
              onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
              className="w-full pl-8 pr-4 py-3 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="10,000"
            />
          </div>
        </div>

        {suggestedAmount > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Suggested:</span> Deploy{' '}
              <button
                onClick={() => onChange(Math.round(suggestedAmount))}
                className="font-semibold underline hover:no-underline"
              >
                ${suggestedAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </button>{' '}
              to reach target allocation.
            </p>
          </div>
        )}

        {amount > 0 && (
          <div className="text-sm text-gray-500 space-y-1">
            <p>
              This is{' '}
              <span className="font-medium text-gray-900">
                {((amount / portfolioValue) * 100).toFixed(1)}%
              </span>{' '}
              of your portfolio value.
            </p>
            {thesis && thesis.target_allocation_pct !== null && (
              <p>
                New allocation would be{' '}
                <span className="font-medium text-gray-900">
                  {(
                    ((thesis.total_market_value + amount) /
                      (portfolioValue + amount)) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
